import type { ReactNode, MouseEventHandler } from 'react';

import { Paper } from '@mui/material';

import withStyles from '@mui/styles/withStyles';
import { Link } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { getPathName } from '@openclassrooms/routing';
import IconButton from '@classify/react-core-components/IconButton';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import Divider from '@classify/react-core-components/Divider';
import Button from '@classify/react-core-components/Button';

import { useMediaQuery } from '@mui/material';
import { useTheme } from '@classify/react-core-components/styles';
import Logo, { LOGO_VARIANTS } from '../Logo';
import type { BackCta } from '../..';

const styles = /* istanbul ignore next */ ({ breakpoints, spacing }) => ({
  root: {
    height: spacing(7),
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
  },

  logoRoot: {
    width: 24,
    margin: [[0, spacing(3)]],
    display: 'block',
  },

  mainWrapper: {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
  },

  content: {
    flexGrow: 1,
  },

  logo: {
    display: 'none',
  },

  logoC: {
    display: 'block',
  },

  logoCompactContainer: {
    display: 'flex',
    alignItems: 'center',
  },

  logoRootCompact: {
    width: 32,
    height: 32,
    display: 'block',
  },

  [breakpoints.up('sm')]: {
    root: {
      height: 64,
    },

    logoRoot: {
      margin: [[0, spacing(5), 0, 0]],
      width: 264,
    },

    logo: {
      display: 'block',
    },

    logoC: {
      display: 'none',
    },

    logoRootCompact: {
      width: 40,
      height: 40,
    },
  },
});

type Props = {
  classes: Record<string, string>;
  hasSmallLogo: boolean;
  shouldHideLogo: boolean;
  children?: ReactNode;
  isWide: boolean;
  onClickLogo: MouseEventHandler<HTMLAnchorElement>;
  backCta?: BackCta;
};

function BaseHeader({
  classes = {},
  hasSmallLogo = false,
  shouldHideLogo = false,
  isWide = false,
  onClickLogo = () => {},
  children,
  backCta,
}: Props) {
  let logo;
  const Wrapper = isWide ? 'div' : ContentWrapper;
  if (!shouldHideLogo) {
    logo = hasSmallLogo ? (
      <div className={classes.logoCompactContainer}>
        <a href={getPathName('/')} onClick={onClickLogo}>
          <Logo
            variant={LOGO_VARIANTS.COMPACT_BACKGROUND}
            classes={{ root: classes.logoRootCompact }}
          />
        </a>
      </div>
    ) : (
      <div className={classes.logoContainer}>
        <a href={getPathName('/')} onClick={onClickLogo}>
          <Logo classes={{ root: classes.logoRoot }} className={classes.logo} />
          <Logo
            variant={LOGO_VARIANTS.COMPACT}
            classes={{ root: classes.logoRoot }}
            className={classes.logoC}
          />
        </a>
      </div>
    );
  }

  const { breakpoints } = useTheme();
  const isCompact = useMediaQuery(breakpoints.down('md'));
  const VerticalDivider = () => <Divider orientation="vertical" sx={{ height: 32, marginX: 2 }} />;

  const linkProps = {
    component: backCta?.href ? 'a' : Link,
    [backCta?.href ? 'href' : 'to']: backCta?.href ? backCta?.href : backCta?.to,
  };

  return (
    <Paper className={classes.root} elevation={0} square sx={{ paddingX: isWide ? 2 : 0 }}>
      <Wrapper className={classes.mainWrapper}>
        {isCompact && backCta && (
          <>
            <IconButton tooltip={backCta.label} aria-label={backCta.label} {...linkProps}>
              <ChevronLeftIcon />
            </IconButton>
            <VerticalDivider />
          </>
        )}
        {logo}
        {!isCompact && backCta && (
          <>
            <VerticalDivider />
            <Button variant="tertiary" {...linkProps}>
              {backCta.label}
            </Button>
          </>
        )}
        {children && <div className={classes.content}>{children}</div>}
      </Wrapper>
    </Paper>
  );
}

export default withStyles(styles)(BaseHeader);
